import { Fragment, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { Alert } from '../../../components/ui/Alert';
import { PageHeader, PageShell } from '../../../components/ui/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '../../../components/ui/States';
import { useRegisterBreadcrumbTab } from '../../../context/BreadcrumbContext';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card, CardHeader } from '../../../components/ui/Card';
import { Input, Textarea } from '../../../components/ui/Input';
import { Table, TableFooter, THead, Th, Td, Tr, TTotalRow, isConstant } from '../../../components/ui/Table';
import { useAccess } from '../../../hooks/useAccess';
import { useCustomerDetail, useUpdateCustomer } from '../../../hooks/useCustomers';
import { formatMonthKey, money, monthSpan, planDisplayName } from './formatters';
import { OdooPartnerLinks } from './OdooPartnerLinks';
import { overrideConflictSentence, overrideInvoiceConflict } from './overrideCheck';
import { TileSkeleton } from './TileSkeleton';
import {
  PAYMENT_BADGE,
  PHASE_VARIANT,
  STAGE_BADGE,
  UNKNOWN,
  chaseKindLabel,
  contractBadgeLabel,
  customerSinceLabel,
  dayMonth,
  documentAmount,
  hasInsight,
  insightBadge,
  invoiceInsightBadge,
  redirectSettled,
  insightDraftNote,
  invoiceCounts,
  invoiceStateBadge,
  paidBeforeIssue,
  settledByOf,
  settledDate,
  unnettedCreditNotes,
  creditPairs,
  paidTogether,
  odooContractText,
  isFutureDated,
  isOpenInvoice,
  monthHeading,
  endedStillInMrr,
  mrrLabel,
  netInvoiced,
  netPaid,
  mrrOverrideLabel,
  overrideShortNote,
  odooLegalName,
  outstandingLabel,
  pendingFollowUp,
  reminderLabel,
  remindersPausedByInsight,
  reminderTierLabel,
  shortDate,
  shortDateTime,
  subscriptionPhaseBadge,
  billingNote,
} from './customerLabels';
import type {
  CustomerDetailInvoice,
  CustomerInvoice,
  CustomerMrrMonth,
  CustomerRow,
  OverrideInvoiceConflict,
  CustomerSubscription,
  InvoiceThreadInsight,
  ReminderDraft,
  TimelineEvent,
  TimelineKind,
} from '../../../api/customers';

const BACK_TO_LIST = '/finance/customers';

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * The Odoo instance's origin, taken from any deep link the API already built.
 * The API does not send a partner URL, and the base URL lives in the Odoo
 * integration config, so this is the one place it can be read from without
 * hard-coding a hostname in the web bundle.
 */
function odooOriginFrom(urls: (string | null)[]): string | null {
  for (const url of urls) {
    if (!url) continue;
    try {
      return new URL(url).origin;
    } catch {
      // Not an absolute URL; try the next one.
    }
  }
  return null;
}

/** Same shape the API uses for partner links elsewhere: `/web#id=…&model=res.partner`. */
function odooPartnerUrl(origin: string | null, odooPartnerId: number | null): string | null {
  if (!origin || odooPartnerId === null) return null;
  return `${origin}/web#id=${odooPartnerId}&model=res.partner`;
}

function ExternalLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-text-secondary hover:text-text-primary hover:underline"
    >
      {children}
    </a>
  );
}


/**
 * Copies text to the clipboard and says so for a moment. A clipboard failure
 * (no permission, http origin) is shown, not swallowed — the whole point of
 * the button is that Walter pastes what it copied.
 */
function CopyButton({
  text,
  label,
  variant = 'secondary',
}: {
  text: string;
  label: string;
  variant?: 'primary' | 'secondary' | 'ghost';
}) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');

  useEffect(() => {
    if (state === 'idle') return;
    const t = setTimeout(() => setState('idle'), 1800);
    return () => clearTimeout(t);
  }, [state]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setState('copied');
    } catch {
      setState('failed');
    }
  };

  return (
    <Button variant={variant} size="sm" onClick={copy} type="button">
      {state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy failed: select and copy by hand' : label}
    </Button>
  );
}

/** A date cell: "12 Mar 2026", or an empty cell (one empty marker on the page: blank). */
function DateCell({ iso }: { iso: string | null | undefined }) {
  return iso ? <>{shortDate(iso)}</> : null;
}

/**
 * A subscription's state in words. A running contract is the normal case, so it
 * reads as plain text; anything else (scheduled, ended, cancelled) is the
 * exception and keeps its pill. Odoo's raw key ("3_progress") is not shown.
 */
function SubscriptionState({ phase, renewedBy }: { phase: CustomerSubscription['phase']; renewedBy?: string | null }) {
  if (phase === 'live') return <span className="text-text-secondary">Running</span>;
  const badge = subscriptionPhaseBadge(phase, renewedBy);
  return (
    <span title={badge.title}>
      <Badge variant={badge.variant}>{badge.label}</Badge>
    </span>
  );
}

/**
 * The API formats a deal value with cents ("€30,000.00"); every other amount on
 * the page is whole euros, so round it here.
 */
function roundMoneyText(text: string): string {
  return text.replace(/(\d[\d,]*)\.(\d{2})\b/g, (_m, whole: string, cents: string) => {
    const value = Math.round(Number(`${whole.replace(/,/g, '')}.${cents}`));
    return value.toLocaleString('en-US');
  });
}

/** An open invoice past its due date needs action, so it starts expanded; the rest start closed. */
function needsAction(invoice: CustomerDetailInvoice): boolean {
  if (!isOpenInvoice(invoice) || invoice.muted) return false;
  if (invoice.overdueDays && invoice.overdueDays > 0) return true;
  return !!invoice.dueDate && invoice.dueDate.slice(0, 10) < new Date().toISOString().slice(0, 10);
}

/**
 * The timeline as the page shows it, whatever API build sent it: a raw tier
 * key in a title ("Reminder drafted: due_today") reads in words, a credit note
 * reads as money going back (−€30,000), an invoice due on the day it was
 * issued is one event, and a reminder drafted after the payment's date says
 * why (GELITA: paid 16 Sep, matched in Odoo on 23 Sep, reminder on 22 Sep).
 */
export function normalizeTimeline(events: TimelineEvent[], invoices: CustomerInvoice[]): TimelineEvent[] {
  const byNumber = new Map(invoices.map((i) => [i.invoiceNumber, i]));
  const issuedOn = new Map(
    events.filter((e) => e.kind === 'invoice_issued' && e.invoiceNumber).map((e) => [e.invoiceNumber!, e.at.slice(0, 10)]),
  );
  return events
    .filter((e) => !(e.kind === 'invoice_due' && e.invoiceNumber && issuedOn.get(e.invoiceNumber) === e.at.slice(0, 10)))
    .map((e) => {
      const inv = e.invoiceNumber ? byNumber.get(e.invoiceNumber) : undefined;
      let { title, detail } = e;
      const raw = /^Reminder drafted: ([a-z0-9_]+)$/.exec(title);
      if (raw) title = `Reminder drafted: ${reminderTierLabel(raw[1]!)}`;
      // A past digest item is history, not today's state ("Nothing outstanding" is the card above).
      if (e.kind === 'chase_raised') title = title.replace(/^Chased: /, 'Chase digest raised: ');
      if (e.kind === 'credit_note' && inv?.isCreditNote && detail && !/^[-−]/.test(detail.trim())) detail = `−${detail.trim()}`;
      if (e.kind === 'reminder_drafted' && inv && settledByOf(inv) === 'payment') {
        const paid = settledDate(inv);
        if (paid && paid.slice(0, 10) < e.at.slice(0, 10)) {
          detail = `Superseded by the payment of ${shortDate(paid)}, not yet matched in Odoo when this was drafted`;
        }
      }
      if (e.kind === 'invoice_paid' && inv && paidBeforeIssue(inv) && detail && !detail.includes('before')) {
        detail = `${detail} · payment received before the invoice date`;
      }
      return title === e.title && detail === e.detail ? e : { ...e, title, detail };
    });
}

/** Timeline kinds whose detail is an amount, which the API sends with cents. */
const MONEY_DETAIL_KINDS = new Set<TimelineKind>(['deal_won', 'invoice_issued', 'invoice_due', 'invoice_paid', 'credit_note']);

/** A timeline detail as the page reads elsewhere: plan names as plans, amounts in whole euros. */
function timelineDetail(event: TimelineEvent): string | null {
  if (!event.detail) return null;
  if (event.kind === 'subscription_started' || event.kind === 'subscription_ended' || event.kind === 'subscription_ends') {
    const plan = planDisplayName(event.detail);
    return plan.toLowerCase() === 'yearly' ? null : plan;
  }
  return MONEY_DETAIL_KINDS.has(event.kind) ? roundMoneyText(event.detail) : event.detail;
}

/** "Nothing to do" is the API's healthy default: show it as an empty value. */
const isNothingToDo = (step: string | null) => step === null || /^nothing to do\.?$/i.test(step.trim());

// ─── Summary strip ───────────────────────────────────────────────────────────

function SummaryCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Card padding="sm" className="min-w-0">
      <p className="eyebrow mb-2">{label}</p>
      <div className="space-y-1 text-body-sm">{children}</div>
    </Card>
  );
}

/** "Jul–Sep 26" for an override's span; open-ended spans read "from Jul 26". */
function spanLabel(from: string, to: string | null): string {
  if (!to) return `from ${formatMonthKey(from)}`;
  return from === to ? formatMonthKey(from) : monthSpan([from, to]);
}

/**
 * The invoices this page can see are the ones on the linked Odoo partners.
 * When the customer was won well before the first of them, earlier invoicing
 * sits on a partner that is not linked here, and the tile says so rather than
 * reading as the customer's whole history.
 */
export function invoicingScope(customer: CustomerRow): string | null {
  // A credit note is not the start of invoicing: GELITA's 19 Mar credit note
  // sits before its first real invoice (20 Aug).
  const dates = customer.invoicing.invoices.filter((i) => !i.isCreditNote).map((i) => i.issueDate.slice(0, 10)).sort();
  const first = dates[0];
  const partners = customer.odooPartners.length;
  if (!first) return null;
  const base = `since ${shortDate(first)} · ${plural(partners, 'linked Odoo partner')}`;
  const wonDays = (Date.parse(first) - Date.parse(customer.closedWonAt)) / 86_400_000;
  return wonDays > 60
    ? `${base} · customer since ${shortDate(customer.closedWonAt)}, no posted invoice in Odoo before ${shortDate(first)}`
    : base;
}

/**
 * The next step, with muted invoices winning: an invoice taken out of the
 * reminder ladder (Swiss GRC, "complex case, handled separately") is never
 * "Chase …", whatever API build computed the step.
 */
/** "…(muted 2026-08-27)" → "…(muted 27 Aug 2026)": one date format on the page. */
function mutedReasonText(reason: string): string {
  return reason.replace(/\b(\d{4}-\d{2}-\d{2})\b/g, (iso) => shortDate(iso));
}

export function displayNextStep(customer: CustomerRow, invoices: CustomerInvoice[]): string | null {
  const step = customer.nextStep;
  if (isNothingToDo(step) || step === null) return null;
  if (!/^chase\b/i.test(step.trim())) return step;
  const muted = invoices.find((i) => i.muted && isOpenInvoice(i) && step.includes(i.invoiceNumber));
  if (!muted) return step;
  return `Handled separately: ${muted.invoiceNumber}${muted.overdueDays ? ` (${muted.overdueDays}d overdue)` : ''}, reminders muted`;
}

function SummaryStrip({
  customer,
  mrrHistory,
  invoices,
}: {
  customer: CustomerRow;
  mrrHistory: CustomerMrrMonth[];
  /** The detail's invoices: they carry `muted` on every API build. */
  invoices: CustomerInvoice[];
}) {
  const latest = mrrHistory[mrrHistory.length - 1];
  const lastOverride = [...mrrHistory].reverse().find((p) => p.override !== null);
  const sub = customer.subscription;
  const inv = customer.invoicing;
  const unlinked = customer.odooCompanyId === null;
  const inMrrAfterEnd = endedStillInMrr(customer);
  // Scheduled invoices (dated after today) are booked, not open: nobody owes them yet.
  const openCount = inv.invoices.filter((i) => isOpenInvoice(i) && !i.scheduled).length;
  const scheduledCount = inv.invoices.filter((i) => i.scheduled && !i.isCreditNote).length;

  return (
    <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
      <SummaryCard label="Contract">
        {sub.phase ? (
          <>
            {/* A live contract is the normal case ("Active" sits in the header), so only an exception gets a pill. */}
            {(sub.phase !== 'live' || billingNote(customer)) && (
              <p>
                {sub.phase !== 'live' && (
                  <Badge variant={PHASE_VARIANT[sub.phase]}>{contractBadgeLabel(sub.phase, sub.startsOn)}</Badge>
                )}
                {billingNote(customer) && (
                  <span className={(sub.phase !== 'live' ? 'ml-2 ' : '') + 'text-text-tertiary'}>
                    {billingNote(customer)}
                  </span>
                )}
              </p>
            )}
            {sub.phase === 'ended' && !inMrrAfterEnd ? (
              // Nothing counts any more: say the state in words, not "— / mo".
              <p className="text-text-primary font-medium tabular-nums">
                {sub.expiresOn ? `Ended ${shortDate(sub.expiresOn)}` : 'Ended'}
              </p>
            ) : inMrrAfterEnd ? (
              <>
                <p className="text-text-primary font-medium tabular-nums">{money(sub.reportedMrr ?? 0, 'EUR')} / mo</p>
                <p className="text-text-tertiary">{inMrrAfterEnd.text}</p>
              </>
            ) : (
              <p className="text-text-primary font-medium tabular-nums">{mrrLabel(customer)} / mo</p>
            )}
            {mrrOverrideLabel(customer) && (
              <p
                className="text-text-tertiary"
                title={`The MRR sheet's reporting override replaces the Odoo amount this month: ${sub.mrrOverride?.note ?? ''}`}
              >
                {mrrOverrideLabel(customer)}
              </p>
            )}
            {latest?.restored && lastOverride?.override && (
              <p className="text-text-tertiary" title={lastOverride.override.note}>
                Rate restored this month: {spanLabel(lastOverride.override.from, lastOverride.override.to)} counted{' '}
                {money(lastOverride.mrr, 'EUR')} against {money(lastOverride.odooMrr, 'EUR')} in Odoo
              </p>
            )}
            <p className="text-text-tertiary whitespace-nowrap">
              {sub.startsOn ? `starts ${shortDate(sub.startsOn)}` : ''}
              {sub.startsOn && sub.expiresOn && sub.phase !== 'ended' ? ' · ' : ''}
              {sub.expiresOn && sub.phase !== 'ended' ? `ends ${shortDate(sub.expiresOn)}` : ''}
            </p>
            {/* An ended contract bills nothing more, whatever Odoo's leftover date says. */}
            {sub.nextInvoiceDate && sub.phase !== 'ended' && (
              <p className="text-text-tertiary whitespace-nowrap">next invoice {shortDate(sub.nextInvoiceDate)}</p>
            )}
          </>
        ) : (
          <p className="text-text-muted">{unlinked ? UNKNOWN : 'No contract in Odoo yet'}</p>
        )}
      </SummaryCard>

      <SummaryCard label="Invoicing">
        {unlinked ? (
          <p className="text-text-muted">{UNKNOWN}</p>
        ) : inv.invoices.length === 0 ? (
          // Nothing posted: one plain line, not a stack of €0s.
          <>
            <p className="text-text-secondary">No posted invoice in Odoo</p>
            {inv.unposted && inv.unposted.cancelled + inv.unposted.draft > 0 && (
              <p className="text-text-tertiary">
                {[
                  inv.unposted.cancelled > 0 ? `${inv.unposted.cancelled} cancelled` : null,
                  inv.unposted.draft > 0 ? `${plural(inv.unposted.draft, 'draft')} never posted` : null,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </p>
            )}
          </>
        ) : (
          <>
            {/* Net of credit notes, so the card agrees with the documents below (Arendt: €114,000 − €18,000). */}
            <p className="text-text-primary font-medium tabular-nums">{money(netInvoiced(inv), 'EUR')} invoiced</p>
            {(inv.totalCredited ?? 0) > 0 && (
              <p className="text-text-tertiary tabular-nums">
                {money(inv.totalInvoiced, 'EUR')} on invoices − {money(inv.totalCredited ?? 0, 'EUR')} in credit notes
              </p>
            )}
            <p
              className="text-text-secondary tabular-nums"
              title={
                (inv.totalCredited ?? 0) > 0
                  ? 'Settled invoices less credit notes: a credit note settles the invoice it reverses without money moving'
                  : undefined
              }
            >
              {money(netPaid(inv), 'EUR')} paid
            </p>
            <p className="text-text-tertiary">
              {plural(inv.created, 'invoice')}
              {invoiceCounts(customer).credited > 0 && ` · ${invoiceCounts(customer).credited} cleared by a credit note`} ·{' '}
              {openCount > 0 ? `${openCount} open` : 'none open'}
            </p>
            {invoicingScope(customer) && (
              <p className="text-text-tertiary break-words">
                {invoicingScope(customer)}
              </p>
            )}
            {scheduledCount > 0 && (
              <p className="text-text-tertiary" title="Booked in Odoo but dated after today: not counted as invoiced or outstanding yet">
                + {money(inv.scheduled, 'EUR')} scheduled on {plural(scheduledCount, 'future-dated invoice')}
              </p>
            )}
          </>
        )}
      </SummaryCard>

      <SummaryCard label="Outstanding">
        <p
          className={
            'font-medium ' + (inv.outstanding > 0 ? 'text-text-primary' : 'text-text-muted')
          }
        >
          {outstandingLabel(customer) === '—' ? 'Nothing outstanding' : outstandingLabel(customer)}
        </p>
        {/* Only overdue earns a pill; "Outstanding" or "Paid" would repeat the amount above. */}
        {customer.paymentStatus === 'overdue' && (
          <p>
            <Badge variant={PAYMENT_BADGE.overdue.variant}>
              {PAYMENT_BADGE.overdue.label}
              {inv.oldestOverdueDays ? ` · ${inv.oldestOverdueDays}d` : ''}
            </Badge>
          </p>
        )}
        {inv.outstandingIncomplete && (
          <p className="text-text-tertiary">an invoice has no residual synced; this is a floor</p>
        )}
      </SummaryCard>

      <SummaryCard label="Next step">
        {displayNextStep(customer, invoices) ? (
          <p className="font-medium text-text-primary">{displayNextStep(customer, invoices)}</p>
        ) : (
          <p className="text-text-muted">
            {inMrrAfterEnd ? `Contract ended: leaves MRR after ${inMrrAfterEnd.month}` : customer.stage === 'former' ? 'Contract ended, nothing to do' : 'Nothing to do'}
          </p>
        )}
        {invoices.some((i) => i.muted && isOpenInvoice(i)) && (
          <p className="text-text-tertiary">
            Reminders muted: {mutedReasonText(invoices.find((i) => i.muted && isOpenInvoice(i))!.muted!.reason)}
          </p>
        )}
        {/* An order the step already names is not said twice. */}
        {customer.uninvoicedOrders
          .filter((o) => !(displayNextStep(customer, invoices) ?? '').includes(o.orderName))
          .map((o) => (
          <p key={o.subscriptionId} className="text-warning-dark">
            {o.orderName} was due to invoice {shortDate(o.nextInvoiceOn)} ({o.daysLate} days ago), nothing invoiced in Odoo
          </p>
        ))}
        {customer.lastChased && (
          <p className="text-text-tertiary">
            Raised in the chase digest on {shortDate(customer.lastChased.at)}: {chaseKindLabel(customer.lastChased.kind)}
          </p>
        )}
      </SummaryCard>
    </div>
  );
}

// ─── MRR in effect, month by month ───────────────────────────────────────────

/**
 * What the Overview counts for this customer each month, beside Odoo's
 * contracts alone. Only shown when the two ever differ: then the override
 * (Xerox Jul–Sep 26 at €9,242 against €14,075 in Odoo) reads the same here as
 * on the Overview, and the month the rate came back says so.
 */
/** The one order running in a month when it is billed in another currency: the amount in that currency. */
function foreignOrderIn(month: string, subscriptions: CustomerSubscription[]): CustomerSubscription | null {
  const running = subscriptions.filter(
    (s) =>
      (s.phase === 'live' || s.phase === 'ended') &&
      s.startsOn !== null &&
      s.startsOn.slice(0, 7) <= month &&
      (s.endsOn === null || s.endsOn.slice(0, 7) >= month),
  );
  const latest = running.sort((a, b) => (b.startsOn ?? '').localeCompare(a.startsOn ?? ''))[0];
  return latest && latest.sourceCurrency !== 'EUR' && latest.sourceMrr !== null ? latest : null;
}

/**
 * Newest first, with consecutive months that read identically (same override,
 * same amounts) folded into one row spanning them: five "same override" rows
 * say one thing. `p` is the newest month of the run, `firstMonth` the oldest.
 */
export function groupedMonths(history: CustomerMrrMonth[]): { p: CustomerMrrMonth; firstMonth: string; count: number }[] {
  const out: { p: CustomerMrrMonth; firstMonth: string; count: number }[] = [];
  for (const p of [...history].reverse()) {
    const last = out[out.length - 1];
    const same =
      last !== undefined &&
      p.override !== null &&
      last.p.override !== null &&
      p.override.note === last.p.override.note &&
      p.override.from === last.p.override.from &&
      p.override.to === last.p.override.to &&
      Math.abs(p.mrr - last.p.mrr) < 0.5 &&
      Math.abs(p.odooMrr - last.p.odooMrr) < 0.5;
    if (same) {
      last.firstMonth = p.month;
      last.count += 1;
    } else {
      out.push({ p, firstMonth: p.month, count: 1 });
    }
  }
  return out;
}

function MrrInEffect({
  history,
  error,
  subscriptions,
}: {
  history: CustomerMrrMonth[];
  error: string | null;
  subscriptions: CustomerSubscription[];
}) {
  if (error) return <ErrorState compact title={`MRR history unavailable: ${error}`} />;
  const differs = history.some((p) => p.override !== null || Math.abs(p.mrr - p.odooMrr) >= 0.5);
  if (!differs) return null;
  return (
    <div className="mt-6">
      <p className="mb-2 text-body-sm font-medium text-text-secondary">MRR in effect, as the Overview counts it</p>
      <Table colWidths={['9rem', '7rem', '12rem', 'auto']}>
        <THead>
          <tr>
            <Th>Month</Th>
            <Th align="right">In MRR</Th>
            <Th align="right">Odoo contracts, EUR</Th>
            <Th>Why they differ</Th>
          </tr>
        </THead>
        <tbody>
          {groupedMonths(history).map(({ p, firstMonth, count }) => (
            <Tr key={p.month}>
              <Td className="tabular-nums">
                {count > 1 ? `${formatMonthKey(firstMonth)} – ${formatMonthKey(p.month)}` : formatMonthKey(p.month)}
              </Td>
              <Td align="right" className="tabular-nums font-medium text-text-primary">{money(p.mrr, 'EUR')}</Td>
              <Td align="right" className="tabular-nums text-text-secondary">
                {money(p.odooMrr, 'EUR')}
                {/* A USD order is converted: its own amount says what Odoo actually holds. */}
                {foreignOrderIn(p.month, subscriptions) && (
                  <span className="block text-body-xs text-text-tertiary">
                    {money(foreignOrderIn(p.month, subscriptions)!.sourceMrr!, foreignOrderIn(p.month, subscriptions)!.sourceCurrency)} in Odoo
                  </span>
                )}
              </Td>
              <Td className="text-body-xs text-text-tertiary">
                {p.override ? (
                  <span className="break-words" title={p.override.note}>
                    {Math.abs(p.mrr - p.odooMrr) < 0.5
                      ? 'Same as Odoo this month'
                      : `Override ${spanLabel(p.override.from, p.override.to)}: ${overrideShortNote(p.override.note)}`}
                    {count > 1 && ` (same in each of the ${count} months)`}
                  </span>
                ) : p.restored ? (
                  'Rate restored: the override ended'
                ) : Math.abs(p.mrr - p.odooMrr) >= 0.5 ? (
                  'Renewal handover or spread setup fee'
                ) : null}
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

// ─── Open invoices with the reminder draft ───────────────────────────────────

function draftBasisNote(draft: ReminderDraft, insight: InvoiceThreadInsight | null): string {
  switch (draft.basis) {
    case 'reached':
      return 'Due now';
    case 'next':
      return draft.firesOn ? `Fires on ${dayMonth(draft.firesOn)}` : 'Next in the ladder';
    case 'overdue_gap':
      return draft.firesOn
        ? `Past due, offered now; next automatic draft on ${dayMonth(draft.firesOn)}`
        : 'Past due, offered now';
    case 'insight':
      return insightDraftNote(insight);
  }
}

/**
 * What the customer's latest mail on the thread says, ahead of the draft: if
 * they wrote that the payment went out, that is the first thing to read, not
 * the reminder the ladder would send next.
 */
function InsightBlock({ insight, settled }: { insight: InvoiceThreadInsight; settled: boolean }) {
  // A settled redirect keeps its story (what they asked, what we did) but loses the amber pill.
  const tag = settled ? null : insightBadge(insight.kind);
  if (!tag && !settled) return null;
  const checkOn = pendingFollowUp(insight);
  const needsReply = insight.kind === 'dispute' || insight.kind === 'question';
  const redirect = insight.kind === 'redirect_recipient';

  return (
    <div className="rounded-xl border border-border-light bg-surface-primary p-4 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-body-sm font-medium text-text-primary">What the customer said</h4>
        {tag && <Badge variant={tag.variant}>{tag.label}</Badge>}
        {checkOn && <Badge variant="pill">Check on {dayMonth(checkOn)}</Badge>}
        {needsReply && <Badge variant="pill">Needs your reply</Badge>}
      </div>
      <p className="text-body-sm text-text-primary">{insight.summary}</p>
      <p className="text-body-sm font-medium text-text-primary">{insight.effect}</p>
      {redirect && !settled && insight.recipients.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {insight.recipients.map((r) => (
            <Badge key={r} variant="default">
              {r}
            </Badge>
          ))}
          <span className="text-body-xs text-text-tertiary">Draft below uses these recipients</span>
        </div>
      )}
      <p className="text-body-xs text-text-tertiary">
        From the mail of {shortDateTime(insight.mailAt)}
        {insight.mailFrom ? ` by ${insight.mailFrom}` : ''} · confidence {insight.confidence}
      </p>
      {insight.evidence && (
        <blockquote className="border-l-2 border-border-dark pl-3 text-body-xs italic text-text-secondary">
          “{insight.evidence}”
        </blockquote>
      )}
    </div>
  );
}

function DraftBlock({ draft, insight }: { draft: ReminderDraft; insight: InvoiceThreadInsight | null }) {
  const to = draft.to.join(', ');
  const all = `To: ${to}\nSubject: ${draft.subject}\n\n${draft.body}`;

  return (
    <div className="rounded-xl border border-border-light bg-surface-secondary p-4 space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="text-body-sm font-medium text-text-primary">
          Reminder draft: {reminderTierLabel(draft.tier)}
        </h4>
        <span className="text-body-xs text-text-tertiary">{draftBasisNote(draft, insight)}</span>
      </div>

      <div className="grid grid-cols-[auto_1fr_auto] items-center gap-x-3 gap-y-2 text-body-sm">
        <span className="text-text-tertiary">To</span>
        <span className={'min-w-0 break-all ' + (to ? 'text-text-primary' : 'text-text-muted')}>
          {to || 'no recipient found on the invoice'}
        </span>
        <CopyButton text={to} label="Copy" variant="ghost" />

        <span className="text-text-tertiary">Subject</span>
        <span className="min-w-0 text-text-primary">{draft.subject}</span>
        <CopyButton text={draft.subject} label="Copy" variant="ghost" />
      </div>

      <pre className="whitespace-pre-wrap rounded-lg border border-border-light bg-surface-primary p-3 font-mono text-body-xs leading-relaxed text-text-primary">
        {draft.body}
      </pre>

      <div className="flex flex-wrap items-center gap-2">
        <CopyButton text={all} label="Copy all (To + Subject + Body)" variant="primary" />
        <CopyButton text={draft.body} label="Copy body" />
        {draft.greetingGuessed && (
          <span className="text-body-xs text-text-tertiary">greeting guessed, check the name</span>
        )}
      </div>

      <p className="text-body-xs text-text-tertiary">{draft.basedOn}</p>
    </div>
  );
}

function MailThread({ invoice }: { invoice: CustomerDetailInvoice }) {
  const [open, setOpen] = useState(false);
  if (invoice.mails.length === 0) {
    return <p className="text-body-xs text-text-tertiary">No mails on this invoice in Odoo.</p>;
  }
  // The inbound mail the insight was read from gets its badge inline, so the thread says which one it was.
  const insight = hasInsight(invoice) ? invoice.insight : null;
  const insightTag = insight ? invoiceInsightBadge(invoice) : null;
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-body-xs text-text-secondary hover:text-text-primary"
      >
        <span className="inline-block w-3 text-text-tertiary">{open ? '▾' : '▸'}</span>
        Mail thread ({invoice.mails.length})
      </button>
      {open && (
        <ul className="mt-2 divide-y divide-border-light border-t border-border-light">
          {invoice.mails.map((mail, idx) => (
            <li key={`${mail.at}-${idx}`} className="py-2 text-body-xs">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-text-tertiary whitespace-nowrap">{shortDateTime(mail.at)}</span>
                <span className="text-text-tertiary" title={mail.direction === 'out' ? 'Sent' : 'Received'}>
                  {mail.direction === 'out' ? '→' : '←'}
                </span>
                <span className="font-medium text-text-primary">{mail.subject ?? '(no subject)'}</span>
                <span className="text-text-tertiary">
                  {mail.direction === 'out'
                    ? mail.to.length
                      ? `to ${mail.to.join(', ')}`
                      : ''
                    : mail.author
                      ? `from ${mail.author}`
                      : ''}
                </span>
                {insight && insightTag && mail.direction === 'in' && mail.at === insight.mailAt && (
                  <Badge variant={insightTag.variant}>{insightTag.label}</Badge>
                )}
              </div>
              {mail.excerpt && <p className="mt-0.5 text-text-secondary">{mail.excerpt}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * What an open invoice needs beyond its table row: the reminders already
 * drafted, what the customer said, the next draft to send and the mail thread.
 */
function OpenInvoiceDetail({ invoice }: { invoice: CustomerDetailInvoice }) {
  const doc = documentAmount(invoice);
  const insight = hasInsight(invoice) ? invoice.insight : null;
  const paused = insight && remindersPausedByInsight(insight) ? insight : null;
  const partial =
    invoice.residual !== null && invoice.residual > 0.005 && invoice.residual < invoice.total - 0.005;
  return (
    <div className="space-y-4 rounded-lg bg-surface-secondary/50 p-4">
      <p className="text-body-xs text-text-tertiary">
        {invoice.termDays}-day terms
        {invoice.overdueDays ? <span className="text-error-dark"> · {invoice.overdueDays} days overdue</span> : null}
        {doc && ` · ${doc} on the document`}
        {partial && ` · ${money(invoice.residual!, invoice.currency)} still open`}
        {invoice.status === 'draft' && ' · still a draft in Odoo'}
      </p>

      <div>
        <p className="text-body-xs text-text-tertiary mb-1">Reminders drafted by the automation</p>
        {invoice.reminders.length === 0 ? (
          <p className="text-body-xs text-text-muted">none yet</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {invoice.reminders.map((r, idx) => (
              <span
                key={`${r.tier}-${r.at}-${idx}`}
                title={r.skippedAsHandled ? 'Skipped, a mail for this tier was already sent from Odoo' : undefined}
              >
                <Badge variant={r.skippedAsHandled ? 'pill' : 'default'}>
                  {reminderTierLabel(r.tier)}
                  {r.at ? ` · ${dayMonth(r.at)}` : ''}
                  {r.skippedAsHandled ? ' · skipped, mail already sent' : ''}
                </Badge>
              </span>
            ))}
          </div>
        )}
      </div>

      {invoice.muted && (
        <p className="text-body-xs text-text-tertiary">
          Reminders muted for this invoice: {mutedReasonText(invoice.muted.reason)}
        </p>
      )}

      {insight && <InsightBlock insight={insight} settled={redirectSettled(invoice)} />}

      {paused && (
        <p className="text-body-xs text-text-tertiary">
          Automatic reminders are paused
          {paused.followUpOn ? ` until ${dayMonth(paused.followUpOn)}` : ''} on the customer's word.
        </p>
      )}

      {invoice.draft && !invoice.muted ? (
        <DraftBlock draft={invoice.draft} insight={insight} />
      ) : (
        <p className="text-body-xs text-text-tertiary">
          {invoice.muted
            ? 'No reminder draft: reminders are muted for this invoice.'
            : invoice.status === 'draft'
            ? 'No reminder draft: the invoice has not been posted in Odoo yet.'
            : isFutureDated(invoice)
              ? 'No reminder draft: the invoice is dated in the future.'
              : 'No reminder draft for this invoice.'}
        </p>
      )}

      <MailThread invoice={invoice} />
    </div>
  );
}

// ─── Timeline ────────────────────────────────────────────────────────────────

const TIMELINE_DOT: Record<TimelineKind, string> = {
  deal_won: 'bg-success',
  subscription_started: 'bg-text-primary',
  subscription_ends: 'bg-text-primary',
  subscription_ended: 'bg-text-primary',
  invoice_issued: 'bg-text-secondary',
  invoice_sent: 'bg-text-secondary',
  invoice_due: 'bg-text-secondary',
  invoice_paid: 'bg-success',
  credit_note: 'bg-text-muted',
  mail_out: 'bg-border-dark',
  mail_in: 'bg-border-dark',
  reminder_drafted: 'bg-warning',
  chase_raised: 'bg-error',
};

const TIMELINE_KIND_LABEL: Record<TimelineKind, string> = {
  deal_won: 'deal',
  subscription_started: 'subscription',
  subscription_ends: 'subscription',
  subscription_ended: 'subscription',
  invoice_issued: 'invoice',
  invoice_sent: 'invoice',
  invoice_due: 'invoice',
  invoice_paid: 'invoice',
  credit_note: 'invoice',
  mail_out: 'mail',
  mail_in: 'mail',
  reminder_drafted: 'reminder',
  chase_raised: 'chase',
};

const TIMELINE_COLLAPSED = 5;

/** Events bucketed by month under a heading, in the order given. */
function TimelineMonths({ events }: { events: TimelineEvent[] }) {
  const groups: { key: string; heading: string; items: TimelineEvent[] }[] = [];
  for (const event of events) {
    const key = event.at.slice(0, 7);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(event);
    else groups.push({ key, heading: monthHeading(event.at), items: [event] });
  }
  return (
    <>
      {groups.map((group) => (
        <div key={group.key}>
          <p className="text-body-sm font-medium text-text-secondary mb-2">{group.heading}</p>
          <ul className="divide-y divide-border-light">
            {group.items.map((event, idx) => (
              <li
                key={`${event.at}-${event.kind}-${idx}`}
                className={'flex items-start gap-3 py-2 text-body-sm ' + (event.future ? 'opacity-60' : '')}
              >
                <span className="w-20 shrink-0 whitespace-nowrap text-text-tertiary">{shortDate(event.at)}</span>
                <span
                  className={'mt-1.5 h-2 w-2 shrink-0 rounded-full ' + TIMELINE_DOT[event.kind]}
                  title={TIMELINE_KIND_LABEL[event.kind]}
                  aria-label={TIMELINE_KIND_LABEL[event.kind]}
                />
                <span className="min-w-0 flex-1">
                  <span className="text-text-primary">{event.title}</span>
                  {event.detail && (
                    <span className="block text-body-xs text-text-tertiary">
                      {timelineDetail(event)}
                    </span>
                  )}
                </span>
                {event.url && (
                  <a
                    href={event.url}
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0 text-text-muted hover:text-text-primary"
                    title="Open"
                  >
                    ↗
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
}

/**
 * Starts at today and reads back in time. What is still to come (dues, contract
 * ends, scheduled invoices) folds under "Upcoming (N)" above it, so the first
 * thing on the card is what happened last, not an invoice due next spring.
 */
function Timeline({ events }: { events: TimelineEvent[] }) {
  const [showAll, setShowAll] = useState(false);
  const [showUpcoming, setShowUpcoming] = useState(false);
  if (events.length === 0) {
    return <p className="text-body-sm text-text-tertiary">Nothing recorded yet.</p>;
  }
  // Newest first: what happened last is what the reader came for.
  const newestFirst = [...events].reverse();
  const upcoming = newestFirst.filter((e) => e.future);
  const past = newestFirst.filter((e) => !e.future);
  const shown = showAll ? past : past.slice(0, TIMELINE_COLLAPSED);
  return (
    <div className="space-y-5">
      {upcoming.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowUpcoming((v) => !v)}
            aria-expanded={showUpcoming}
            className="flex items-center gap-1 text-body-sm font-medium text-text-secondary hover:text-text-primary"
          >
            <ChevronRight
              className={'h-3.5 w-3.5 transition-transform ' + (showUpcoming ? 'rotate-90' : '')}
              aria-hidden
            />
            Upcoming ({upcoming.length})
          </button>
          {showUpcoming && (
            <div className="mt-3 space-y-5">
              <TimelineMonths events={upcoming} />
            </div>
          )}
        </div>
      )}
      {upcoming.length > 0 && (
        <p className="eyebrow border-t border-border-light pt-3">Today and before</p>
      )}
      {past.length === 0 ? (
        <p className="text-body-sm text-text-tertiary">Nothing has happened yet.</p>
      ) : (
        <TimelineMonths events={shown} />
      )}
      {past.length > TIMELINE_COLLAPSED && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="text-body-xs font-medium text-text-tertiary hover:text-text-primary"
        >
          {showAll ? 'Show less' : `Show all ${past.length} events`}
        </button>
      )}
    </div>
  );
}

// ─── Odoo links & notes ──────────────────────────────────────────────────────

function LinksAndNotes({ customer }: { customer: CustomerRow }) {
  const update = useUpdateCustomer();
  const [orderFormUrl, setOrderFormUrl] = useState(customer.orderFormUrl ?? '');
  const [notes, setNotes] = useState(customer.notes ?? '');
  const [saved, setSaved] = useState(false);

  const dirty = orderFormUrl !== (customer.orderFormUrl ?? '') || notes !== (customer.notes ?? '');

  const save = async () => {
    await update.mutateAsync({
      id: customer.id,
      orderFormUrl: orderFormUrl.trim() || null,
      notes: notes.trim() || null,
    });
    setSaved(true);
  };

  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => setSaved(false), 1800);
    return () => clearTimeout(t);
  }, [saved]);

  return (
    <Card padding="md" className="space-y-5">
      <CardHeader title="Odoo links & notes" />
      {update.error && <Alert variant="error">Could not save: {update.error.message}</Alert>}
      <OdooPartnerLinks customer={customer} />
      <div className="space-y-3">
        <Input
          label="Order form (reference only)"
          value={orderFormUrl}
          onChange={(e) => setOrderFormUrl(e.target.value)}
          placeholder="https://drive.google.com/…"
          hint="Where the signed order form lives, for the next person. It does not gate any step."
        />
        <Textarea
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Billing entity, PO number, anything the next person needs."
        />
        <div className="flex items-center gap-3">
          <Button onClick={save} isLoading={update.isPending} disabled={!dirty}>
            Save
          </Button>
          {saved && <span className="text-body-xs text-text-tertiary">Saved</span>}
        </div>
      </div>
    </Card>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

/**
 * One customer, in full: what the contract is, every invoice with the mails
 * Odoo sent on it and the reminder the automation would send next, the whole
 * history in date order, and the Odoo links the row is read from.
 */
export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { canAccess } = useAccess();
  const { data, isLoading, error, refetch } = useCustomerDetail(id ?? null);
  // Invoices the reader flipped away from their default (open when it needs action, closed otherwise).
  const [toggled, setToggled] = useState<Set<string>>(new Set());
  // The breadcrumb holds one level, so it names the customer: "Finance › Acme".
  // "Customers" is the back link right under it.
  useRegisterBreadcrumbTab(data?.customer.companyName ?? 'Customers');

  // Same rule as the tab: the customer list is its own access feature.
  if (!canAccess('finance.customers')) {
    return <Navigate to="/finance/overview" replace />;
  }

  const back = (
    <Link
      to={BACK_TO_LIST}
      className="inline-flex items-center gap-1 text-body-sm text-text-secondary hover:text-text-primary"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden /> Customers
    </Link>
  );

  if (!id || error || isLoading || !data) {
    return (
      <PageShell>
        {back}
        {!id ? (
          <ErrorState title="Missing customer id" />
        ) : error ? (
          <ErrorState title="Couldn’t load the customer" error={error} onRetry={() => refetch()} />
        ) : isLoading ? (
          <div className="space-y-6">
            {/* The header's place, so the tiles do not jump down when the name arrives. */}
            <PageHeader eyebrow="Finance · Customer" title="Loading…" meta={<span>&nbsp;</span>}>
              <p className="text-body-sm">&nbsp;</p>
            </PageHeader>
            <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
              {['Contract', 'Invoicing', 'Outstanding', 'Next step'].map((label) => (
                <TileSkeleton key={label} label={label} />
              ))}
            </div>
            <LoadingState rows={6} />
          </div>
        ) : (
          <Card padding="lg">
            <EmptyState
              title="No such customer"
              description={
                <>
                  Nothing in the customer list has the id <span className="font-mono">{id}</span>. It may have been
                  archived, or the link is stale.
                </>
              }
              action={
                <Link to={BACK_TO_LIST} className="text-body-sm text-text-secondary hover:underline">
                  Back to the customer list
                </Link>
              }
            />
          </Card>
        )}
      </PageShell>
    );
  }

  const { customer, subscriptions, invoices, timeline, odooError, mrrHistory, mrrError } = data;
  // Scheduled invoices are not open yet: nobody owes them.
  const openCount = invoices.filter((i) => isOpenInvoice(i) && !i.scheduled).length;
  // A column that is empty on every row is dropped rather than shown as a column of dots.
  const showReminder = invoices.some((i) => i.reminder !== null);
  const showInsight = invoices.some((i) => invoiceInsightBadge(i) !== null);
  const lastCol = !showReminder && !showInsight;
  // A column that says the same on every row (all "Paid", all sent) is said
  // once in the card's description instead.
  const pairs = creditPairs(invoices);
  const stateOf = (i: CustomerInvoice) => invoiceStateBadge(i, pairs.get(i.invoiceNumber) ?? null);
  const statusConstant = isConstant(invoices, (i) => stateOf(i).label);
  const showStatus = !statusConstant || invoices.length === 1;
  const sentConstant = isConstant(invoices, (i) => i.sent);
  const showSent = !sentConstant || invoices.length === 1;
  const showSettled = invoices.some((i) => settledDate(i) !== null);
  const showOpen = invoices.some((i) => !i.scheduled && (i.residual === null || Math.abs(i.residual) >= 0.005));
  const columnCount =
    4 + [showStatus, showSent, showSettled, showOpen, showReminder, showInsight].filter(Boolean).length;
  const unnetted = unnettedCreditNotes(invoices);
  const unnettedTotal = invoices
    .filter((i) => unnetted.has(i.invoiceNumber))
    .reduce((sum, i) => sum + i.total, 0);
  const invoicesDescription = [
    plural(invoices.length, 'document'),
    statusConstant && invoices.length > 1 ? `all ${stateOf(invoices[0]!).label.toLowerCase()}` : openCount > 0 ? `${openCount} open` : 'none open',
    sentConstant && invoices.length > 1 && invoices[0]!.sent === true ? 'all sent from Odoo' : null,
    'newest first',
  ]
    .filter(Boolean)
    .join(' · ');
  const shownTimeline = normalizeTimeline(timeline, invoices);
  const overrideConflict = conflictOf(customer, subscriptions, mrrHistory, invoices);
  const isYearly = (plan: string) => planDisplayName(plan).toLowerCase() === 'yearly';
  const showPlan = subscriptions.some((s) => s.planName && !isYearly(s.planName));
  const showNextInvoice = subscriptions.some(
    (s) => s.phase !== 'ended' && (s.nextInvoiceOn !== null || s.invoiceDueSince !== null),
  );
  const mrrSplit = splitOrderMrr(subscriptions, customer.subscription.reportedMrr);
  const orderMrr = mrrSplit.byOrder;
  // A contract that ended this month but still counts in its MRR (nextbike): the order says so in its own cell.
  const endedInMrr = endedStillInMrr(customer);
  // An In MRR column with nothing in it (every order ended, EMIS) is left out.
  const showInMrr = subscriptions.some((s) => orderMrr.has(s.id)) || endedInMrr !== null;
  const odooOrigin = odooOriginFrom([
    ...subscriptions.map((s) => s.odooUrl),
    ...invoices.map((i) => i.odooUrl),
  ]);
  const toggleInvoice = (invoiceId: string) =>
    setToggled((prev) => {
      const next = new Set(prev);
      if (next.has(invoiceId)) next.delete(invoiceId);
      else next.add(invoiceId);
      return next;
    });

  return (
    <PageShell>
      {back}

      <PageHeader eyebrow="Finance · Customer"
        title={customer.companyName}
        meta={
          <>
            <Badge variant={STAGE_BADGE[customer.stage].variant}>{STAGE_BADGE[customer.stage].label}</Badge>
            {odooLegalName(customer) && (
              <span className="text-text-tertiary" title="The Odoo partner's legal name">
                {odooLegalName(customer)}
              </span>
            )}
            <span>
              {customerSinceLabel(customer)} · {plural(customer.deals.length, 'deal')}
              {customer.deals.length > 1 && ` (latest ${shortDate(customer.latestWonAt)})`}
              {customer.ownerName && ` · ${customer.ownerName}`}
            </span>
          </>
        }
      >
        <p className="flex flex-wrap gap-x-4 gap-y-1 text-body-sm">
          <ExternalLink href={customer.attioUrl}>Deal in Attio ↗</ExternalLink>
          {customer.deals.length > 1 &&
            customer.deals
              .filter((d) => d.attioUrl !== customer.attioUrl)
              .map((d) => (
                <ExternalLink key={d.id} href={d.attioUrl}>
                  {d.dealName ?? 'Deal'} ({shortDate(d.closedWonAt)}) ↗
                </ExternalLink>
              ))}
          {customer.odooPartners.map((p) => {
            const href = odooPartnerUrl(odooOrigin, p.odooPartnerId);
            return href ? (
              <ExternalLink key={p.companyId} href={href}>
                {p.name} in Odoo ↗
              </ExternalLink>
            ) : (
              <span key={p.companyId} className="text-text-tertiary">
                {p.name} in Odoo
              </span>
            );
          })}
        </p>
      </PageHeader>

      {/* One warning at most: an unlinked customer has no Odoo mail to fail on. */}
      {customer.odooCompanyId === null ? (
        <Alert variant="warning">
          Not linked to an Odoo partner, so contract and invoice figures are missing, not zero. Link it under
          Odoo links &amp; notes below, or archive it if the deal predates Odoo.
        </Alert>
      ) : odooError ? (
        <ErrorState
          compact
          title="Mail threads could not be loaded from Odoo"
          error={`${odooError}. The reminder drafts below may be missing or built without the mail history.`}
        />
      ) : null}

      {overrideConflict && (
        <Alert variant="warning">
          MRR override and invoice disagree. {overrideConflictSentence(overrideConflict)}
        </Alert>
      )}

      <SummaryStrip customer={customer} mrrHistory={mrrHistory} invoices={invoices} />

      <section>
        <Card padding="md">
          <CardHeader
            title="Invoices"
            description={invoices.length ? invoicesDescription : undefined}
          />
          {invoices.length === 0 ? (
            <p className="text-body-sm text-text-tertiary">
              {customer.odooCompanyId === null ? 'Unknown: no Odoo record.' : 'No posted invoice in Odoo yet.'}
            </p>
          ) : (
            <Table minWidth={showReminder || showInsight ? 960 : 760}>
              <THead>
                <tr>
                  <Th>Invoice</Th>
                  {showStatus && <Th>Status</Th>}
                  {showSent && <Th align="center">Sent</Th>}
                  <Th>Issued</Th>
                  <Th>Due</Th>
                  {showSettled && <Th>Settled</Th>}
                  <Th align="right" className={lastCol && !showOpen ? '!pr-2' : undefined}>Total</Th>
                  {/* As the last column it sits at the scroller's edge; a little padding keeps the bold header from clipping. */}
                  {showOpen && <Th align="right" className={lastCol ? '!pr-2' : undefined}>Still open</Th>}
                  {showReminder && <Th>Reminder</Th>}
                  {showInsight && <Th>Insight</Th>}
                </tr>
              </THead>
              <tbody>
                {invoices.map((invoice) => {
                  const state = stateOf(invoice);
                  const doc = documentAmount(invoice);
                  const insightTag = invoiceInsightBadge(invoice);
                  const isOpen = isOpenInvoice(invoice);
                  const expanded = isOpen && needsAction(invoice) !== toggled.has(invoice.id);
                  const settled = settledDate(invoice);
                  const early = paidBeforeIssue(invoice);
                  const notNetted = unnetted.has(invoice.invoiceNumber);
                  const against = pairs.get(invoice.invoiceNumber) ?? null;
                  return (
                    <Fragment key={invoice.id}>
                    <Tr>
                      <Td className="whitespace-nowrap">
                        {isOpen ? (
                          <button
                            type="button"
                            onClick={() => toggleInvoice(invoice.id)}
                            aria-expanded={expanded}
                            aria-label={expanded ? 'Hide reminder and mails' : 'Show reminder and mails'}
                            className="mr-1 inline-flex align-middle text-text-tertiary hover:text-text-primary"
                          >
                            <ChevronRight className={`h-4 w-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                          </button>
                        ) : (
                          <span className="mr-1 inline-block w-4" />
                        )}
                        <span className="font-medium text-text-primary">
                          {invoice.odooUrl ? (
                            <ExternalLink href={invoice.odooUrl}>{invoice.invoiceNumber}</ExternalLink>
                          ) : (
                            invoice.invoiceNumber
                          )}
                        </span>
                        {invoice.isCreditNote && <span className="ml-1.5 text-body-xs text-text-tertiary">credit note</span>}
                        {against && (
                          <span className="block pl-5 text-body-xs text-text-tertiary">
                            {invoice.isCreditNote ? `clears ${against}` : `cleared by ${against}`}
                          </span>
                        )}
                        {notNetted && (
                          <span className="block pl-5 text-body-xs text-text-tertiary whitespace-normal">
                            not netted: dated before every invoice listed, so it reverses none of them
                          </span>
                        )}
                      </Td>
                      {showStatus && (
                        <Td className="whitespace-nowrap">
                          <Badge variant={state.variant}>{state.label}</Badge>
                        </Td>
                      )}
                      {showSent && (
                        <Td align="center">
                          {invoice.sent === null ? (
                            <span className="text-text-muted" title="Not synced yet">?</span>
                          ) : invoice.sent ? (
                            <span className="text-text-primary" aria-label="sent">✓</span>
                          ) : null}
                        </Td>
                      )}
                      <Td className="whitespace-nowrap tabular-nums"><DateCell iso={invoice.issueDate} /></Td>
                      <Td className="whitespace-nowrap tabular-nums">
                        {invoice.dueDate.slice(0, 10) === invoice.issueDate.slice(0, 10) ? (
                          <span className="text-text-tertiary">on issue</span>
                        ) : (
                          <DateCell iso={invoice.dueDate} />
                        )}
                      </Td>
                      {showSettled && (
                        <Td className="whitespace-nowrap tabular-nums">
                          <DateCell iso={settled} />
                          {early && (
                            <span
                              className="block text-body-xs text-text-tertiary"
                              title="Odoo matched a bank payment dated before the invoice: the money came in first and the invoice was booked after it."
                            >
                              before the invoice date
                            </span>
                          )}
                        </Td>
                      )}
                      <Td align="right" className={'whitespace-nowrap tabular-nums' + (lastCol && !showOpen ? ' !pr-2' : '')}>
                        {money(invoice.total, invoice.currency)}
                        {doc && <span className="block text-body-xs text-text-tertiary">{doc}</span>}
                      </Td>
                      {showOpen && (
                        <Td align="right" className={'whitespace-nowrap tabular-nums' + (lastCol ? ' !pr-2' : '')}>
                          {invoice.scheduled ? (
                            // Dated after today: booked, not owed yet, so nothing is open on it (Xerox INV/2027/00001).
                            null
                          ) : invoice.residual === null ? (
                            <span className="text-text-muted">{UNKNOWN}</span>
                          ) : Math.abs(invoice.residual) < 0.005 ? null : (
                            money(invoice.residual, invoice.currency)
                          )}
                        </Td>
                      )}
                      {showReminder && (
                        <Td className="whitespace-nowrap">
                          {invoice.reminder ? <span className="text-text-secondary">{reminderLabel(invoice)}</span> : null}
                        </Td>
                      )}
                      {showInsight && (
                        <Td className="whitespace-nowrap">
                          {insightTag && hasInsight(invoice) ? (
                            <span title={invoice.insight.effect}>
                              <Badge variant={insightTag.variant}>{insightTag.label}</Badge>
                            </span>
                          ) : null}
                        </Td>
                      )}
                    </Tr>
                    {expanded && (
                      <tr className="border-b border-border-light">
                        <td colSpan={columnCount} className="pb-4 pt-1">
                          <OpenInvoiceDetail invoice={invoice} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </Table>
          )}
          {invoices.length > 0 && (
            <TableFooter>
              {plural(invoices.length, 'document')} · {money(netInvoiced(customer.invoicing), 'EUR')} invoiced
              {(customer.invoicing.totalCredited ?? 0) > 0 && ' net of credit notes'}
              {unnettedTotal !== 0 &&
                ` (${money(unnettedTotal, 'EUR')} in ${plural(unnetted.size, 'credit note')} not netted: ${[...unnetted].join(', ')} predates the invoices listed)`}{' '}
              · {outstandingLabel(customer) === '—' ? 'nothing open' : `${outstandingLabel(customer)} open`}
            </TableFooter>
          )}
          {paidTogether(invoices).map((line) => (
            <p key={line} className="mt-1 text-body-xs text-text-tertiary">
              {line}
            </p>
          ))}
        </Card>
      </section>

      <section>
        <Card padding="md">
          <CardHeader title="Timeline" description="newest first" />
          <Timeline events={shownTimeline} />
        </Card>
      </section>

      <section>
        <Card padding="md">
          <CardHeader
            title="Subscriptions"
            description={subscriptions.length ? plural(subscriptions.length, 'order') : undefined}
          />
          {subscriptions.length === 0 ? (
            <p className="text-body-sm text-text-tertiary">
              {customer.odooCompanyId === null ? 'Unknown: no Odoo record.' : 'No subscription in Odoo.'}
            </p>
          ) : (
            <Table minWidth={720}>
              <THead>
                <tr>
                  <Th>Order</Th>
                  {showPlan && <Th>Plan</Th>}
                  <Th>State</Th>
                  <Th>Starts</Th>
                  <Th>Ends</Th>
                  {showNextInvoice && <Th>Next invoice</Th>}
                  {showInMrr && <Th align="right">In MRR</Th>}
                </tr>
              </THead>
              <tbody>
                {subscriptions.map((sub) => {
                  const inMrr = orderMrr.get(sub.id);
                  return (
                  <Tr key={sub.id}>
                    <Td className="font-medium text-text-primary whitespace-nowrap">
                      {sub.odooUrl ? <ExternalLink href={sub.odooUrl}>{sub.odooOrderName} ↗</ExternalLink> : sub.odooOrderName}
                    </Td>
                    {showPlan && (
                      <Td>
                        {sub.planName && !isYearly(sub.planName) ? (
                          <span title={sub.planName}>{planDisplayName(sub.planName)}</span>
                        ) : null}
                      </Td>
                    )}
                    <Td className="whitespace-nowrap">
                      <SubscriptionState phase={sub.phase} renewedBy={sub.renewedBy} />
                    </Td>
                    <Td className="whitespace-nowrap tabular-nums"><DateCell iso={sub.startsOn} /></Td>
                    <Td className="whitespace-nowrap tabular-nums"><DateCell iso={sub.endsOn} /></Td>
                    {showNextInvoice && (
                      <Td className="whitespace-nowrap tabular-nums">
                        <DateCell iso={sub.phase === 'ended' ? null : sub.nextInvoiceOn} />
                        {sub.invoiceDueSince && (
                          <span className="ml-2">
                            <Badge variant="warning">Not invoiced</Badge>
                          </span>
                        )}
                      </Td>
                    )}
                    {/* Only a running order is in MRR, and at the rate MRR counts it: after the
                        MRR sheet's override (Swiss GRC S00189: €1,250, Odoo €1,875), so the
                        column reads the header's figure. */}
                    {showInMrr && (
                    <Td
                      align="right"
                      className="whitespace-nowrap tabular-nums"
                      title={sub.phase !== 'live' && sub.mrr !== null ? `${money(sub.mrr, 'EUR')} / mo on the order, not in MRR now` : undefined}
                    >
                      {inMrr ? (
                        <>
                          <span className={inMrr.counted === null ? 'text-text-secondary' : undefined}>
                            {inMrr.counted !== null ? money(inMrr.counted, 'EUR') : inMrr.odooText}
                          </span>
                          {inMrr.counted !== null && inMrr.differs && (
                            <span className="block text-body-xs text-text-tertiary">Odoo {inMrr.odooText}</span>
                          )}
                          {inMrr.counted === null && mrrSplit.overridden && (
                            <span className="block text-body-xs text-text-tertiary">Odoo rate</span>
                          )}
                        </>
                      ) : sub.phase === 'ended' && endedInMrr ? (
                        <>
                          <span>{money(customer.subscription.reportedMrr ?? 0, 'EUR')}</span>
                          <span className="block text-body-xs text-text-tertiary">for {endedInMrr.month}, then out</span>
                        </>
                      ) : null}
                    </Td>
                    )}
                  </Tr>
                  );
                })}
              </tbody>
              {mrrSplit.overridden && mrrSplit.liveCount > 1 && customer.subscription.reportedMrr !== null && (
                <tfoot>
                  <TTotalRow>
                    <Td colSpan={showNextInvoice ? 6 : 5}>In MRR this month, after the MRR sheet&apos;s override</Td>
                    <Td align="right" className="tabular-nums">{money(customer.subscription.reportedMrr, 'EUR')}</Td>
                  </TTotalRow>
                </tfoot>
              )}
            </Table>
          )}
          <LiveOrdersNote subscriptions={subscriptions} reportedMrr={mrrSplit.overridden ? customer.subscription.reportedMrr : null} />
          <MrrInEffect history={mrrHistory} error={mrrError} subscriptions={subscriptions} />
        </Card>
      </section>

      <LinksAndNotes customer={customer} />
    </PageShell>
  );
}

/**
 * Two or more live orders at once all count in MRR. Usually that is a renewal
 * handover the engine collapses; when it is not (Dawsongroup: S00219 from
 * 22 Sep and S00210 from 1 Oct, both yearly) the sum is what MRR carries, so
 * the page names each order and its amount rather than leaving the reader to
 * add the rows up.
 */
function LiveOrdersNote({
  subscriptions,
  reportedMrr,
}: {
  subscriptions: CustomerSubscription[];
  /** Set when the MRR sheet overrides this month's total, so the note does not claim the Odoo sum. */
  reportedMrr: number | null;
}) {
  const live = subscriptions.filter((s) => s.phase === 'live');
  if (live.length < 2) return null;
  const parts = live.map((s) => {
    const amount = s.mrr !== null ? money(s.mrr, 'EUR') : s.sourceMrr !== null ? `${money(s.sourceMrr, s.sourceCurrency)} unconverted` : 'amount unknown';
    const due = s.invoiceDueSince ? `, due to invoice ${shortDate(s.invoiceDueSince)}, not invoiced` : '';
    return `${s.odooOrderName} ${amount} from ${s.startsOn ? shortDate(s.startsOn) : 'unknown start'}${due}`;
  });
  // Only two live orders for the same amount look like a renewal that was
  // never closed. Different amounts are an extension running alongside
  // (Dawsongroup S00219 next to S00210), so there is nothing to fix in Odoo.
  const looksLikeDuplicate = live.some((a, i) =>
    live.some((b, j) => j > i && sameAmount(a, b) && a.renewedBy !== b.odooOrderName && b.renewedBy !== a.odooOrderName),
  );
  return (
    <p className="mt-3 text-body-xs text-text-secondary">
      {live.length} live orders, each counted in MRR: {parts.join(' · ')}.
      {looksLikeDuplicate
        ? ' Two of them carry the same amount: if one replaces the other, end the old one in Odoo or link the renewal.'
        : reportedMrr !== null
          ? ` They run side by side; this month the MRR sheet's override counts ${money(reportedMrr, 'EUR')} for them together.`
          : ' They run side by side for different amounts, so the total is what MRR carries.'}
    </p>
  );
}

/**
 * What each order adds to this month's MRR, so the column reconciles with the
 * header. Without an override the Odoo amounts are what MRR counts. With one
 * (Swiss GRC €1,250 against €1,875 in Odoo; ISS North America €3,807 against
 * €6,300), a single live order carries the counted figure; several live orders
 * keep their Odoo rates, marked as such, and a total row carries the counted
 * figure, since the sheet overrides the customer, not one order. Pure.
 */
export function splitOrderMrr(
  subscriptions: CustomerSubscription[],
  reportedMrr: number | null,
): {
  byOrder: Map<string, { counted: number | null; odooText: string; differs: boolean }>;
  overridden: boolean;
  liveCount: number;
} {
  const live = subscriptions.filter((s) => s.phase === 'live');
  const odooSum = live.every((s) => s.mrr !== null) ? live.reduce((sum, s) => sum + (s.mrr ?? 0), 0) : null;
  const overridden = reportedMrr !== null && odooSum !== null && Math.abs(reportedMrr - odooSum) >= 0.5;
  const byOrder = new Map<string, { counted: number | null; odooText: string; differs: boolean }>();
  for (const s of live) {
    const odooText =
      s.mrr !== null
        ? odooContractText(s.mrr, s.sourceMrr, s.sourceCurrency)
        : s.sourceMrr !== null
          ? `${money(s.sourceMrr, s.sourceCurrency)} · unconverted`
          : UNKNOWN;
    if (!overridden) byOrder.set(s.id, { counted: s.mrr, odooText, differs: false });
    else if (live.length === 1) byOrder.set(s.id, { counted: reportedMrr, odooText, differs: true });
    else byOrder.set(s.id, { counted: null, odooText, differs: true });
  }
  return { byOrder, overridden, liveCount: live.length };
}

/**
 * The override checked against the invoice for its term: the API's reading
 * when it sends one, else the same rule here (`overrideCheck.ts`).
 */
export function conflictOf(
  customer: CustomerRow,
  subscriptions: CustomerSubscription[],
  history: CustomerMrrMonth[],
  invoices: CustomerInvoice[],
): OverrideInvoiceConflict | null {
  const fromApi = customer.subscription.mrrOverride?.invoiceConflict;
  if (fromApi !== undefined) return fromApi;
  const override = history[history.length - 1]?.override ?? null;
  const live = subscriptions.filter((s) => s.phase === 'live');
  if (!override || live.length !== 1) return null;
  return overrideInvoiceConflict({
    override: { mrr: override.mrr, from: override.from, to: override.to },
    order: { start: live[0]!.startsOn, end: live[0]!.endsOn },
    invoices: invoices.map((i) => ({
      invoiceNumber: i.invoiceNumber,
      issueDate: i.issueDate,
      total: i.total,
      documentTotal: i.documentTotal,
      documentCurrency: i.documentCurrency,
      isCreditNote: i.isCreditNote,
      credited: settledByOf(i) === 'credit_note',
    })),
  });
}

/** Same monthly amount within 1% (or a euro); false when either is unknown. */
function sameAmount(a: CustomerSubscription, b: CustomerSubscription): boolean {
  const x = a.mrr ?? (a.sourceCurrency === b.sourceCurrency ? a.sourceMrr : null);
  const y = b.mrr ?? (a.sourceCurrency === b.sourceCurrency ? b.sourceMrr : null);
  if (x === null || y === null || y === 0) return false;
  return Math.abs(x - y) <= Math.max(1, Math.abs(y) * 0.01);
}
